import { useState } from 'react';
import { Copy, ExternalLink, Eye, EyeOff } from 'lucide-react';

/**
 * Block of labeled credential rows. Used wherever we display WP/DB creds
 * (CreateSite success reveal, SiteDetail re-reveal).
 *
 * `items`: array of { label, value, sensitive?, isLink? }
 *   - sensitive=true → value hidden behind dots with an eye toggle
 *   - isLink=true    → value rendered as an anchor (for admin URLs)
 */
export function CredBlock({ title, items }) {
  return (
    <div className="bg-white rounded-xl border border-bd overflow-hidden">
      <div className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-t3 bg-s-2 border-b border-bd">
        {title}
      </div>
      <div className="divide-y divide-bd">
        {items.map((it) => (
          <CredRow key={it.label} {...it} />
        ))}
      </div>
    </div>
  );
}

export function CredRow({ label, value, sensitive, isLink }) {
  const [visible, setVisible] = useState(!sensitive);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard API may fail in insecure contexts; ignore
    }
  };

  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <span className="text-xs text-t3 w-24 shrink-0">{label}</span>
      <span className="flex-1 font-mono text-sm text-t1 break-all">
        {isLink ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline inline-flex items-center gap-1"
          >
            {value}
            <ExternalLink className="w-3 h-3 text-t3" />
          </a>
        ) : visible ? (
          value
        ) : (
          '•'.repeat(Math.min(24, value?.length || 0))
        )}
      </span>
      {sensitive && (
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="text-t3 hover:text-t2"
          title={visible ? 'Hide' : 'Show'}
        >
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      )}
      <button
        type="button"
        onClick={handleCopy}
        className={[
          'text-xs inline-flex items-center gap-1 px-2 py-1 rounded-md',
          copied
            ? 'text-ok bg-s-green'
            : 'text-t3 hover:text-t1 hover:bg-s-3',
        ].join(' ')}
      >
        <Copy className="w-3 h-3" />
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}
